import React, { FC } from 'react'
import Paper from '@mui/material/Paper'
import format from "date-fns/format"
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { TableToolbar } from 'src/components/ui'
import { WagnerProbe, WagnerProbeCurrentData } from 'src/api'
import { capitalizeFirstLetter } from 'src/utils/formatters'

const columns: GridColDef<AppWagnerProbeReading>[] = [
  { field: 'name', headerName: 'Reading', flex: 1, sortable: false, disableColumnMenu: true },
  { field: 'value', headerName: 'Value', flex: 1, sortable: false, disableColumnMenu: true },
  {
    field: 'readingTime',
    headerName: 'Reading Time',
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: (params) => (params.value ? format(new Date(params.value as string), 'PPpp') : '')
  }
]
interface WagnerProbeLastReadingsProps {
  wagnerProbe: WagnerProbe;
}
export interface AppWagnerProbeReading {
  name: string;
  value: string | number | undefined;
  readingTime: string | undefined;
}
type GetReadingRow = (data: WagnerProbeCurrentData) => AppWagnerProbeReading
interface ReadingRows {
  slabTemperatureF: GetReadingRow
  slabHumidity: GetReadingRow
  signalStrength: GetReadingRow
  grabberBatteryLevel: GetReadingRow
  repeaterBatteryLevel: GetReadingRow
  deviceType: GetReadingRow
  source: GetReadingRow
  receivedFromDataGrabberAt: GetReadingRow
  receivedFromMiniGrabberAt: GetReadingRow
  receivedFromTotalReaderAt: GetReadingRow
  receivedFromRepeaterAt: GetReadingRow
  receivedViaPhoneAt: GetReadingRow
  receivedViaGatAt: GetReadingRow
}

const rows: ReadingRows = {
  slabTemperatureF: (data) => {
    return {
      name: 'Slab Temperature',
      value: data.slabTemperatureF ? `${data.slabTemperatureF}°F` : "-",
      readingTime: data.readingTime,
    }
  },
  slabHumidity: (data) => {
    return {
      name: 'Slab Humidity',
      value: data.slabHumidity ? `${data.slabHumidity}%` : "-",
      readingTime: data.readingTime,
    }
  },
  signalStrength: (data) => {
    return {
      name: 'Signal Strength',
      value: data.signalStrength || "-",
      readingTime: data.readingTime,
    }
  },
  grabberBatteryLevel: (data) => {
    return {
      name: 'Grabber Battery Level',
      value: data.batteryLevel ? `${data.batteryLevel}%` : "-",
      readingTime: data.readingTime,
    }
  },
  repeaterBatteryLevel: (data) => {
    return {
      name: 'Repeater Battery Level',
      value: data.repeaterBatteryLevel ? `${data.repeaterBatteryLevel}%` : "-",
      readingTime: data.readingTime,
    }
  },
  deviceType: (data) => {
    return {
      name: 'Device Type',
      value: data.deviceType ? capitalizeFirstLetter(data.deviceType as string).replace("_", " ") : "-",
      readingTime: data.readingTime,
    }
  },
  source: (data) => {
    return {
      name: 'Source',
      value: capitalizeFirstLetter(data.source as string) || "-",
      readingTime: data.readingTime,
    }
  },
  receivedFromDataGrabberAt: (data) => {
    return {
      name: 'Received From Data Grabber',
      value: data.receivedFromDataGrabberAt ? format(new Date(data.receivedFromDataGrabberAt), "PPpp") : "-",
      readingTime: undefined,
    }
  },
  receivedFromMiniGrabberAt: (data) => {
    return {
      name: 'Received From Mini Grabber',
      value: data.receivedFromMiniGrabberAt ? format(new Date(data.receivedFromMiniGrabberAt), "PPpp") : "-",
      readingTime: undefined,
    }
  },
  receivedFromTotalReaderAt: (data) => {
    return {
      name: 'Received From Total Reader',
      value: data.receivedFromTotalReaderAt ? format(new Date(data.receivedFromTotalReaderAt), "PPpp") : "-",
      readingTime: undefined,
    }
  },
  receivedFromRepeaterAt: (data) => {
    return {
      name: 'Received From Repeater',
      value: data.receivedFromRepeaterAt ? format(new Date(data.receivedFromRepeaterAt), "PPpp") : "-",
      readingTime: undefined,
    }
  },
  receivedViaPhoneAt: (data) => {
    return {
      name: 'Received Via Phone',
      value: data.receivedViaPhoneAt ? format(new Date(data.receivedViaPhoneAt), "PPpp") : "-",
      readingTime: undefined,
    }
  },
  receivedViaGatAt: (data) => {
    return {
      name: 'Received Via GAT',
      value: data.receivedViaGatAt ? format(new Date(data.receivedViaGatAt), "PPpp") : "-",
      readingTime: undefined,
    }
  },
}

export const WagnerProbeLastReadings: FC<WagnerProbeLastReadingsProps> = (props) => {
  const { wagnerProbe } = props
  if (!wagnerProbe) return null

  const data: AppWagnerProbeReading[] = Object.values(rows).map((value: GetReadingRow) => value(wagnerProbe.currentData as WagnerProbeCurrentData || {}))

  return (
    <Paper sx={{ height: '500px' }}>
      <DataGridPremium<AppWagnerProbeReading>
        pagination={false}
        autoHeight={false}
        disableRowSelectionOnClick
        rows={data}
        columns={columns}
        rowCount={data.length}
        getRowId={(row) => row.name}
        rowThreshold={2}
        columnThreshold={2}
        hideFooterPagination
        localeText={{
          columnMenuSortAsc: 'Sort A-Z',
          columnMenuSortDesc: 'Sort Z-A'
        }}
        slots={{
          toolbar: () => <TableToolbar exportedFileName={`${document.title}_last_readings`} />
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            paddingLeft: '5px',
            overflow: 'visible'
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1,
            fontSize: '13px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            overflow: 'visible'
          }
        }}
      />
    </Paper>
  )
}
